import React, { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { isTelusOrg, isChardOrg } from '../../../../users/constant';
import { IoChevronDownSharp } from 'react-icons/io5';
import AmenitiesRichTextEditor from '../../../../../components/AmenitiesRichTextEditor';
import { ErrorText } from '../../../../../components/utils/AvatarCropper';
import { Palette } from '../../../../../components/utils';
import { IconSelector } from '../../../../../components/IconSelector';
import { FieldBlock, FieldInnerFull, FieldLabel } from '../../../../../components/WizardV2/wizardStylesV2';
import {
  DescriptionEditorWrap,
  FieldRest,
  WizardTextInput
} from './addAmenityWizardStylesV2';

function AddAmenityBasicsStepV2({
  t,
  building,
  control,
  errors,
  amenityTypes,
  setValue
}) {
  const { globalOrgId } = useSelector((state) => state.Organization);

  const typeOptions = useMemo(
    () => {
      const telusRegex = /\/[^/]*telus[^/]*$/i;
      const chardRegex = /\/[^/]*chard[^/]*$/i;
      const orgId = building?.org_id || globalOrgId;
      const isTelus = isTelusOrg(orgId);
      const isChard = isChardOrg(orgId);

      return (amenityTypes || [])
        .map((a) => {
          const raw = a.icon;
          const icon =
            raw != null && typeof raw === 'string' && raw.trim() !== '' ? raw.trim() : '';
          return { value: a.ID, label: a.Description, icon };
        })
        .filter((o) => {
          if (isTelus) return telusRegex.test(o.icon);
          if (isChard) return chardRegex.test(o.icon);
          return true;
        });
    },
    [amenityTypes, building, globalOrgId]
  );

  return (
    <>
      <FieldBlock>
        <FieldInnerFull>
          <FieldLabel>{t('Building')}</FieldLabel>
          <FieldRest>
            <WizardTextInput
              readOnly
              tabIndex={-1}
              value={building?.name || ''}
              placeholder="—"
              aria-label={t('Building')}
            />
            <IoChevronDownSharp size={18} color={Palette.CONTENT_TERTIARY} aria-hidden />
          </FieldRest>
        </FieldInnerFull>
      </FieldBlock>

      <FieldBlock>
        <FieldInnerFull>
          <FieldLabel>{t('Amenity Name')}</FieldLabel>
          <FieldRest>
            <Controller
              control={control}
              name="code"
              render={({ field: { onChange, onBlur, value } }) => (
                <WizardTextInput
                  type="text"
                  id="wizard-amenity-code"
                  placeholder={t('Amenity name')}
                  autoComplete="off"
                  onBlur={onBlur}
                  onChange={onChange}
                  value={value || ''}
                />
              )}
            />
          </FieldRest>
        </FieldInnerFull>
        {errors?.code && <ErrorText>{errors.code.message}</ErrorText>}
      </FieldBlock>

      <FieldBlock>
        <FieldInnerFull>
          <FieldLabel>{t('Icon Selector')}</FieldLabel>
          <Controller
            control={control}
            name="type_id"
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <IconSelector
                ref={ref}
                id="wizard-type_id"
                name="type_id"
                options={typeOptions}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                placeholder={t('Please select')}
                hasError={!!errors?.type_id}
              />
            )}
          />
        </FieldInnerFull>
        {errors?.type_id && <ErrorText>{errors.type_id.message}</ErrorText>}
      </FieldBlock>

      <FieldBlock>
        <FieldInnerFull>
          <FieldLabel>{t('Description')}</FieldLabel>
          <DescriptionEditorWrap>
            <Controller
              control={control}
              name="rich_description"
              render={({ field: { onChange, value } }) => (
                <AmenitiesRichTextEditor
                  value={value || ''}
                  onChange={onChange}
                  placeholder={t('Enter description...')}
                  setValue={setValue}
                  fieldName="rich_description"
                />
              )}
            />
          </DescriptionEditorWrap>
        </FieldInnerFull>
        {errors?.rich_description && <ErrorText>{errors.rich_description.message}</ErrorText>}
      </FieldBlock>
    </>
  );
}

export default AddAmenityBasicsStepV2;
